import {useCallback, useMemo} from 'react';
import {CountryOfOrigin, BrandInfo} from '../types';
import {FilterRange} from '../utils';
import {
  filterRanges,
  filterInStockOptions,
  availableForSaleOptions,
} from '../constants';

const brandTagKey = 'brand';

const FixedShopFilterTagPrefix = Object.freeze({
  abv: 'abv',
  abvRange: 'abv',
  age: 'age',
  ageRange: 'age',
  priceRange: 'priceTags',
  averageRatingGroup: 'ratings',
  country: 'countries',
  productGroup: 'list',
  inStock: 'inStock',
  availableForSale: 'availableForSale',
});

const DynamicShopFilterTagPrefix = Object.freeze({
  [brandTagKey]: 'brands',
  style: 'styles',
  type: 'types',
  partnerStore: 'partner_stores',
});

const getNumberArray = ({min, max}: FilterRange): Array<string> =>
  Array.from(Array(max - min + 1).keys()).map((num) => `${min + num}`);

function getNumberRangeArray({min, max}: FilterRange): Array<string> {
  const rangeMin = Math.floor(min / 10);
  const rangeMax = Math.ceil(max / 10);

  return Array.from(Array(rangeMax - rangeMin).keys()).map(
    (key) =>
      `${Math.max((key + rangeMin) * 10, min)}-${Math.min(
        (key + rangeMin + 1) * 10,
        max,
      )}`,
  );
}

const tagReference = {
  abv: getNumberArray(filterRanges.abv), // abv_0, abv_1, ..., abv_100
  abvRange: getNumberRangeArray(filterRanges.abv),
  age: getNumberArray(filterRanges.age), // age_0, age_1, ..., age_80
  ageRange: getNumberRangeArray(filterRanges.age),
  priceRange: ['under75', '75-125', '125-175', '175-225', 'over225'],
  averageRatingGroup: ['5', '4', '3', '2', '1'],
  country: Object.values(CountryOfOrigin).map(({value}) => value),
  inStock: Object.values(filterInStockOptions).map(({value}) => value),
  productGroup: [
    'new-arrivals',
    'recently-updated',
    'highest-rated',
    'trending',
    'twl-exclusive',
    'twl-recommended',
    'popular',
    'on-sale',
  ],
  availableForSale: Object.values(availableForSaleOptions),
  [brandTagKey]: ['Bacardi', 'Aberlour'], // dynamic
  style: ['Peated', 'American Oak'], // dynamic
  type: ['Single Malt', 'Blended Malt'], // dynamic
  partnerStore: ['Amatos', "Dan Murphy's"], // dynamic
};

type DynamicShopFilterTagPrefixType = keyof typeof DynamicShopFilterTagPrefix;
type FixedShopFilterTagPrefixType = keyof typeof FixedShopFilterTagPrefix;
type ShopFilterTagPrefixType =
  | DynamicShopFilterTagPrefixType
  | FixedShopFilterTagPrefixType;

type GetFilterOptionsHandler = {
  (prefix: typeof brandTagKey): Array<{
    value: string;
    label: string;
    isDefault: boolean;
  }>;
  (prefix: Exclude<ShopFilterTagPrefixType, typeof brandTagKey>): Array<{
    value: string;
    label: string;
  }>;
};

interface UseShopifyTagResolverType {
  getFilterOptions: GetFilterOptionsHandler;
  isTagValid: (tag: string) => boolean;
}

export interface DynamicShopFilterTag {
  brands: ReadonlyArray<BrandInfo>;
  styles: ReadonlyArray<string>;
  types: ReadonlyArray<string>;
  partner_stores: ReadonlyArray<string>;
}

function isBrandPrefix(
  prefix: ShopFilterTagPrefixType,
): prefix is typeof brandTagKey {
  return prefix === brandTagKey;
}

/*
  Helper utility For validating/converting ShopifyProduct.tags to usable format in filtering UI
  Exposed as a hook to use some react memoization features
*/
export default function useShopifyTagResolver(
  tags: DynamicShopFilterTag,
): UseShopifyTagResolverType {
  const shopifyTagMap = useMemo(() => {
    const tagMap = new Map<
      ShopFilterTagPrefixType,
      Set<string> | Map<string, boolean>
    >([
      ...(Object.keys(FixedShopFilterTagPrefix).map(
        (key: FixedShopFilterTagPrefixType) => [
          key,
          new Set(tagReference[key]),
        ],
      ) as [FixedShopFilterTagPrefixType, Set<string>][]),
    ]);

    if (!tags) {
      return tagMap;
    }
    for (const key of Object.keys(
      DynamicShopFilterTagPrefix,
    ) as Array<DynamicShopFilterTagPrefixType>) {
      if (key !== brandTagKey) {
        tagMap.set(key, new Set(tags[DynamicShopFilterTagPrefix[key]]));
      } else {
        tagMap.set(
          key,
          new Map<string, boolean>(
            tags[DynamicShopFilterTagPrefix[key]].map(({value, is_default}) => [
              value,
              is_default,
            ]),
          ),
        );
      }
    }

    return tagMap;
  }, [tags]);

  const getFilterOptions = useCallback(
    function getFilterOptions(prefix: ShopFilterTagPrefixType) {
      if (isBrandPrefix(prefix)) {
        const tagMap = shopifyTagMap.get(prefix) as Map<string, boolean>;
        return [...Array.from(tagMap)].map(([value, is_default]) => ({
          value,
          label: value,
          isDefault: is_default,
        }));
      }
      return [...Array.from(shopifyTagMap.get(prefix) as Set<string>)].map(
        (value) => ({
          value,
          label: value,
        }),
      );
    },
    [shopifyTagMap],
  ) as GetFilterOptionsHandler;

  const isTagValid = useCallback(
    (tag: string): boolean => {
      const [key, value] = tag.split('_') as [ShopFilterTagPrefixType, string];

      if (shopifyTagMap.has(key)) {
        return shopifyTagMap.get(key).has(value);
      }
      return false;
    },
    [shopifyTagMap],
  );

  return {getFilterOptions, isTagValid};
}
